import { BaseService, ApiError } from "./BaseService";
import {
    RequestType,
    RequestPriority,
    SupplyItemCategory,
    SupplyItem,
    LocationAddress,
    LocationAddressDTO,
    LocationContact,
    LocationContactSubmit,
    RequestSubmitData,
    Request,
    AttachmentUploadResult,
    CreateRequestResponse,
    CleanedRequestData,
    RequestListFilters,
    RequestListResponse,
    RequestResponseDto,
} from "../models/Request";
import { validateFile, validateTotalSize } from "@/utils/fileValidation";
import logger from "@/utils/logger";
import { mapAttachmentUploadError } from "@/utils/attachmentUploadError";

class RequestServiceClass extends BaseService {
    protected get baseUrl(): string {
        return `${process.env.NEXT_PUBLIC_API_URL}/api/request/`;
    }

    // Extend BaseService with multipart methods
    protected async multipartRequest<T>(
        method: string,
        path: string,
        formData: FormData
    ): Promise<T> {
        try {
            logger.info(`🔄 Starting multipart request: ${method} ${path}`);

            const token = await this.getValidToken();
            logger.info(`🔑 Token obtained: ${token ? "Yes" : "No"}`);

            const url = `${this.baseUrl.replace(/\/$/, "")}/${path.replace(/^\/+/, "")}`;
            logger.info(`🌐 Request URL: ${url}`);

            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    // Don't set Content-Type - let browser handle multipart boundary
                },
                body: formData,
            });

            logger.info(
                `📡 Response status: ${response.status} ${response.statusText}`
            );
            logger.info(
                `📡 Response headers:`,
                Object.fromEntries(response.headers.entries())
            );

            if (!response.ok) {
                const errorText = await response.text().catch(() => "");
                const msg =
                    errorText?.trim() ||
                    `Request failed (${response.status} ${response.statusText})`;

                throw new ApiError({
                    status: response.status,
                    statusText: response.statusText,
                    url,
                    message: msg,
                    bodyText: errorText,
                });
            }

            if (response.status === 204) {
                logger.info(`✅ 204 No Content response`);
                return undefined as unknown as T;
            }

            // Check if response has content
            const contentLength = response.headers.get("content-length");
            const contentType = response.headers.get("content-type");
            logger.info(
                `📄 Content-Length: ${contentLength}, Content-Type: ${contentType}`
            );

            if (contentLength === "0") {
                console.warn(`⚠️ Response has no content (Content-Length: 0)`);
                return {} as T;
            }

            // Get response text first to check if it's empty
            const responseText = await response.text();
            logger.info(`📝 Raw response text length: ${responseText.length}`);
            logger.info(
                `📝 Raw response preview: ${responseText.substring(0, 200)}${responseText.length > 200 ? "..." : ""}`
            );

            if (!responseText || responseText.trim() === "") {
                console.warn(`⚠️ Empty response body received`);
                return {} as T;
            }

            try {
                const jsonResult = JSON.parse(responseText);
                logger.info(`✅ JSON parsed successfully:`, jsonResult);
                return jsonResult;
            } catch (jsonError) {
                console.error(`❌ JSON parse error:`, jsonError);
                console.error(
                    `❌ Response text that failed to parse:`,
                    responseText
                );
                throw new Error(
                    `Failed to parse JSON response: ${jsonError instanceof Error ? jsonError.message : "Unknown JSON error"}`
                );
            }
        } catch (error) {
            console.error(`❌ Multipart request failed:`, error);
            console.error(`❌ Error details:`, {
                message:
                    error instanceof Error ? error.message : "Unknown error",
                stack: error instanceof Error ? error.stack : undefined,
            });
            throw error;
        }
    }

    protected postMultipart<T>(path: string, formData: FormData): Promise<T> {
        return this.multipartRequest<T>("POST", path, formData);
    }

    protected putMultipart<T>(path: string, formData: FormData): Promise<T> {
        return this.multipartRequest<T>("PUT", path, formData);
    }

    async getRequestType(): Promise<RequestType[]> {
        const types = await this.get<RequestType[]>("GetRequestTypeList");
        try {
            localStorage.setItem(
                "dispatchiq_request_types",
                JSON.stringify(types)
            );
        } catch {
            // localStorage may be unavailable; ignore
        }
        return types;
    }

    async getRequestPriority(): Promise<RequestPriority[]> {
        return this.get("GetRequestPriorityList");
    }

    async getSupplyItemCategory(): Promise<SupplyItemCategory[]> {
        return this.get("GetRequestSupplyItemCategoryList");
    }

    async getSupplyItem(): Promise<SupplyItem[]> {
        return this.get("GetRequestSupplyItemList");
    }
    async checkPackageSlipNumberExists(
        packageSlipNumber: number,
        excludeRequestId?: number
    ): Promise<boolean> {
        try {
            // Build URL with optional query parameter
            let url = `/checkPackageSlipNumberExists/${packageSlipNumber}`;

            // Add excludeRequestId as query parameter if provided
            if (excludeRequestId !== undefined && excludeRequestId !== null) {
                url += `?excludeRequestId=${excludeRequestId}`;
            }

            const response: unknown = await this.get(url);
            return response === true || response === "true";
        } catch (error: unknown) {
            const axiosError = error as { response?: { status: number } };
            if (axiosError.response?.status === 404) {
                return false;
            }
            console.error("Error checking package slip number:", error);
            return false;
        }
    }

    async getLocationAddress(inputText: string): Promise<LocationAddress[]> {
        try {
            // Validate and encode input
            if (!inputText || typeof inputText !== "string") {
                console.warn(
                    "⚠️ Invalid input provided to getLocationAddress:",
                    inputText
                );
                return [];
            }

            const trimmedInput = inputText.trim();
            if (trimmedInput.length === 0) {
                console.warn("⚠️ Empty input provided to getLocationAddress");
                return [];
            }

            logger.info(`🔍 Fetching locations for input: "${trimmedInput}"`);

            // Encode the input to handle special characters and spaces
            const encodedInput = encodeURIComponent(trimmedInput);
            const result = await this.get(`GetLocationList/${encodedInput}`);

            logger.info(`✅ Location search result:`, result);

            // Ensure we return an array even if API returns null/undefined
            if (!result) {
                console.warn("⚠️ Location API returned null/undefined");
                return [];
            }

            if (!Array.isArray(result)) {
                console.warn("⚠️ Location API returned non-array:", result);
                return [];
            }

            return result;
        } catch (error) {
            console.error("❌ Error in getLocationAddress:", error);
            console.error("❌ Input was:", inputText);
            return []; // Return empty array on error
        }
    }

    async getLocationAddressByLocationId(
        locationAddressId: number
    ): Promise<LocationAddressDTO[]> {
        return this.get(`GetLocationAddressByLocationId/${locationAddressId}`);
    }

    async getLocationContactByLocationId(
        locationAddressId: number
    ): Promise<LocationContact[]> {
        return this.get(
            `GetLocationContactListByLocationId/${locationAddressId}`
        );
    }

    async getRequestById(requestId: number): Promise<Request> {
        // Assuming your API for getting a single request by ID is just the ID.
        // If it's something like `GetRequestById`, adjust accordingly.
        return this.get<Request>(`${requestId}`);
    }

    async getRequestByIdForTrip(
        requestId: number,
        userId?: number
    ): Promise<RequestResponseDto> {
        // Append userId if provided
        const url = userId ? `${requestId}?userId=${userId}` : `${requestId}`;

        return this.get<RequestResponseDto>(url);
    }

    // Add the missing method that was being called in the original code
    async getRequestDetailsByIdAsync(requestId: number): Promise<Request> {
        // This method calls the detailed endpoint for request information
        return this.get<Request>(`${requestId}/details`);
    }

    // FIX 2: Change to `this.put` and adjust return type (likely a single Request or success message)
    async updateRequest(
        requestId: number,
        data: RequestSubmitData
    ): Promise<Request> {
        // Assuming your API expects a PUT request to the ID endpoint for updates.
        // And it returns the updated Request object. Adjust return type if your API returns something else (e.g., void or a simple success message).
        const cleanedData = this.cleanRequestData(data); // Clean data before sending for update
        logger.info(
            "Transformed Request Data for API:",
            JSON.stringify(cleanedData, null, 2)
        );
        return this.put<Request>(`updateRequest/${requestId}`, cleanedData);
    }

    async getRequestList(
        filters?: RequestListFilters
    ): Promise<RequestListResponse> {
        const requestBody = {
            pageNumber: filters?.pageNumber || 1,
            pageSize: filters?.pageSize || 10,
            sortDirection: filters?.sortDirection || "DESC",
            sortColumn: filters?.sortColumn || "RequestId",
            requestPriorityId: filters?.requestPriorityId || null,
            requestTypeId: filters?.requestTypeId || null,
            requestStatusId: filters?.requestStatusId || null,
            isEligible:
                filters?.isEligible !== undefined ? filters.isEligible : null,
            createdBy: filters?.createdBy || null,
            requestSearch: filters?.requestSearch || null,
            isRequestWithoutTrip: filters?.isRequestWithoutTrip ?? false,
            requestedBy: filters?.requestedBy || null,
        };

        return this.postRetryable("GetRequestList", requestBody, "json", 2);
    }
    async createLocationContact(
        data: LocationContactSubmit,
        locationAddressId: number
    ): Promise<{ locationContactId: number; message: string }> {
        const response = await this.post<LocationContact>(
            `CreateLocationContact/${locationAddressId}`,
            data
        );
        return {
            locationContactId: response.locationContactId,
            message: "Location Contact created successfully!",
        };
    }

    async createRequest(
        data: RequestSubmitData
    ): Promise<{ requestId: number; message: string }> {
        const cleanedData = this.cleanRequestData(data);
        logger.info(
            "Transformed Request Data for API:",
            JSON.stringify(cleanedData, null, 2)
        );
        const response = await this.post<CreateRequestResponse>(
            "CreateRequest",
            cleanedData
        );
        return {
            requestId: response.requestId,
            message: "Request created successfully!",
        };
    }

    async getRequestDetails(requestId: number): Promise<Request> {
        return this.get(`${requestId}/details`);
    }

    // RequestService.ts
    // ...
    async getRequestViewDetails(
        requestId: number,
        userId: number
    ): Promise<unknown> {
        // add userId to the query string so the server can use it
        return this.get(
            `${requestId}/view-details?userId=${encodeURIComponent(userId)}`
        );
    }

    async deleteAttachment(
        requestId: number,
        attachmentId: number
    ): Promise<boolean> {
        await this.delete(`${requestId}/attachments/${attachmentId}`);
        return true;
    }

    async addAttachmentsToRequest(
        requestId: number,
        files: File[]
    ): Promise<AttachmentUploadResult[]> {
        const results: AttachmentUploadResult[] = [];

        const totalSizeValidation = validateTotalSize(files);
        if (!totalSizeValidation.isValid) {
            const message =
                totalSizeValidation.error ??
                "Total attachment size exceeds the allowed limit.";
            return files.map((file) => ({
                fileName: file.name,
                isSuccess: false,
                blobUrl: "",
                fileSize: file.size,
                errorMessage: message,
            }));
        }

        // Validate each file before upload
        for (const file of files) {
            const validation = validateFile(file);
            if (!validation.isValid) {
                results.push({
                    fileName: file.name,
                    isSuccess: false,
                    blobUrl: "",
                    fileSize: file.size,
                    errorMessage: validation.error,
                });
                continue;
            }

            // Upload valid files
            try {
                const formData = new FormData();
                formData.append("files", file);
                const uploadResult = await this.postMultipart<
                    AttachmentUploadResult[]
                >(`${requestId}/attachments`, formData);
                if (uploadResult && uploadResult.length > 0) {
                    results.push(uploadResult[0]);
                }
            } catch (error) {
                const mappedError = mapAttachmentUploadError(error, "request");
                results.push({
                    fileName: file.name,
                    isSuccess: false,
                    blobUrl: "",
                    fileSize: file.size,
                    errorMessage: mappedError.message,
                });
            }
        }

        return results;
    }

    async replaceAttachment(
        requestId: number,
        attachmentId: number,
        newFile: File
    ): Promise<AttachmentUploadResult> {
        const validation = validateFile(newFile);
        if (!validation.isValid) {
            throw new Error(
                validation.error || `File "${newFile.name}" failed validation.`
            );
        }

        const formData = new FormData();
        formData.append("newFile", newFile);
        return this.putMultipart(
            `${requestId}/attachments/${attachmentId}`,
            formData
        );
    }

    // Helper methods
    private hasProperty(obj: unknown, prop: string): boolean {
        return (
            obj !== null &&
            obj !== undefined &&
            typeof obj === "object" &&
            prop in obj
        );
    }

    private extractFileNameFromUrl(url: string): string {
        try {
            const urlObj = new URL(url);
            const segments = urlObj.pathname.split("/");
            return segments[segments.length - 1] || "unknown";
        } catch {
            return "unknown";
        }
    }

    async deleteRequest(
        id: number,
        reason?: {
            requestStatusReasonId: number;
            requestStatusReasonText?: string;
        }
    ): Promise<void> {
        if (reason) {
            // If reason is provided, use DELETE with body
            await this.deleteWithBody(`DeleteRequest/${id}`, reason);
        } else {
            // Fallback to simple DELETE for backward compatibility
            await this.delete(`DeleteRequest/${id}`);
        }
    }

    private cleanRequestData(data: RequestSubmitData): CleanedRequestData {
        const result: CleanedRequestData = {
            RequestTypeId: data.requestTypeId,
            RequestPriorityId: data.requestPriorityId,
            RequestStatusId: data.requestStatusId,
            RequestTypeAttribute: data.requestTypeAttribute,
            RequestAttributeValue: data.requestAttributeValue,
            OriginLocationId: data.originLocationId,
            DestinationLocationId: data.destinationLocationId,
            OriginDt: data.originDt,
            OriginTm: data.originTm,
            OriginDtm: data.originDtm,
            DestinationDt: data.destinationDt,
            DestinationTm: data.destinationTm,
            DestinationDtm: data.destinationDtm,
            IsIrradiation: data.isIrradiation ?? false,
            Instructions: data.instructions || "",
            CreatedBy: data.createdBy,
            RequestSupplyItems: (data.requestSupplyItems || []).map((item) => ({
                SupplyItemId: item.supplyItemId,
                Quantity: item.quantity,
            })),
            RequestAttachments: (data.requestAttachments || []).map((att) => ({
                BlobStorageUrl: att.blobStorageUrl,
            })),
        };

        if (
            data.originContactId !== null &&
            data.originContactId !== undefined
        ) {
            result.OriginContactId = data.originContactId;
        }

        if (
            data.destinationContactId !== null &&
            data.destinationContactId !== undefined
        ) {
            result.DestinationContactId = data.destinationContactId;
        }

        if (data.packingSlipNumbers?.length) {
            result.PackingSlipNumbers = data.packingSlipNumbers;
        }

        return result;
    }

    async checkPackingSlipExists(
        packingSlipNumber: string,
        excludeRequestId?: number
    ): Promise<boolean> {
        let url = `checkPackageSlipNumberExists/${encodeURIComponent(packingSlipNumber)}`;
        if (excludeRequestId !== undefined) {
            url += `?excludeRequestId=${excludeRequestId}`;
        }
        return this.get<boolean>(url);
    }

    async generatePackingSlipNumbers(
        requestTypeId: number,
        count: number
    ): Promise<string[]> {
        return this.get<string[]>(
            `generatePackingSlipNumbers?requestTypeId=${requestTypeId}&count=${count}`
        );
    }

    async getPackingSlips(requestId: number): Promise<string[]> {
        return this.get<string[]>(`${requestId}/packingSlips`);
    }
}

export const requestService = new RequestServiceClass();
